/*
 * WorkAndBuhin.vue の部品名・価格編集モードと価格変更 payload の組み立てを切り出した処理。
 */
import { BuhinDetail } from "@/model/Buhin";
import { findSelectedBuhinIndex } from "@/components/work-buhin/WorkBuhinMatch";

export interface EditModeChange {
  shouldUpdate: boolean;
  editMode: boolean;
  isChange: boolean;
  buhinChangeEntry: any | null;
}

export interface EditModeChangeApplyEffectState {
  shouldUpdate: boolean;
  shouldSetIsChange: boolean;
  isChange: boolean;
  editMode: boolean;
  shouldApplyBuhinChangeEntry: boolean;
  buhinChangeEntry: any | null;
  shouldForceUpdate: boolean;
}

export interface BuhinPriceChange {
  id: any;
  entry: {
    buhinNm: string;
    newBuhinPrice: number | undefined;
    oldBuhinPrice: number | undefined;
  };
}

export interface BuhinPriceChangeActionState {
  shouldSyncSelectedBuhinPrice: boolean;
  selectedBuhinIndex: number;
  selectedBuhinPrice: number;
  shouldApplyPriceChange: boolean;
  buhinChangeId: any;
  buhinChangeEntry: any | null;
}

export interface BuhinPriceChangeApplyEffectState {
  shouldSyncSelectedBuhinPrice: boolean;
  shouldApplySelectedBuhinPrice: boolean;
  selectedBuhinIndex: number;
  selectedBuhinPrice: number;
  shouldApplyPriceChange: boolean;
  buhinChangeId: any;
  buhinChangeEntry: any | null;
}

export interface EditResultActionState {
  shouldStopPropagation: boolean;
  shouldEmitOnEditResult: boolean;
  payload: any;
}

export interface EditResultApplyEffectState {
  shouldStopPropagation: boolean;
  shouldEmitOnEditResult: boolean;
  eventName: string;
  payload: any;
}

export interface BuhinNameInputClickActionState {
  shouldStopPropagation: boolean;
}

/*
 * resolveEditModeChange を WorkAndBuhin.vue から分離し、
 * 編集モードと価格変更追跡 を親側置換や単体テストで固定するため。
 */
export function resolveEditModeChange(params: {
  work: any;
  disabledBtnNoBuhin: boolean;
  currentBuhinChangeEntry: any;
}): EditModeChange {
  if (params.work.buhinNm && params.disabledBtnNoBuhin) {
    return {
      shouldUpdate: false,
      editMode: !!params.work.editMode,
      isChange: false,
      buhinChangeEntry: null
    };
  }

  if (params.work.editMode) {
    const buhinChangeEntry = params.work.buhinNm && params.currentBuhinChangeEntry
      ? {
        ...params.currentBuhinChangeEntry,
        buhinNm: params.work.buhinNm
      }
      : null;
    return {
      shouldUpdate: true,
      editMode: false,
      isChange: true,
      buhinChangeEntry
    };
  }

  return {
    shouldUpdate: true,
    editMode: true,
    isChange: false,
    buhinChangeEntry: null
  };
}

/*
 * buildEditModeChangeApplyEffectState を WorkAndBuhin.vue から分離し、
 * 編集モード変更後に component が実行する isChange、editMode、変更履歴、再描画の反映条件を固定するため。
 */
export function buildEditModeChangeApplyEffectState(
  editModeChange: EditModeChange
): EditModeChangeApplyEffectState {
  return {
    shouldUpdate: editModeChange.shouldUpdate,
    shouldSetIsChange: editModeChange.shouldUpdate && editModeChange.isChange,
    isChange: editModeChange.isChange,
    editMode: editModeChange.editMode,
    shouldApplyBuhinChangeEntry: !!editModeChange.buhinChangeEntry,
    buhinChangeEntry: editModeChange.buhinChangeEntry,
    shouldForceUpdate: editModeChange.shouldUpdate
  };
}

/*
 * buildBuhinPriceChange を WorkAndBuhin.vue から分離し、
 * 編集モードと価格変更追跡 を親側置換や単体テストで固定するため。
 */
export function buildBuhinPriceChange(params: {
  buhin: BuhinDetail;
  isManual: boolean;
  isDialog: boolean;
  workBeforeChange: any;
}): BuhinPriceChange | null {
  if (params.isDialog || !params.buhin.id) {
    return null;
  }

  let buhinList = params.isManual
    ? params.workBeforeChange.manualBuhinList
    : params.workBeforeChange.buhinList;
  if (!params.isManual && params.workBeforeChange.osusumeBuhinList) {
    buhinList = [...buhinList, ...params.workBeforeChange.osusumeBuhinList];
  }

  const oldBuhin = buhinList.find((buhin: BuhinDetail) => buhin.id == params.buhin.id);
  if (!oldBuhin) {
    return null;
  }
  return {
    id: params.buhin.id,
    entry: {
      buhinNm: params.buhin.buhinNm,
      newBuhinPrice: params.buhin.buhinPriceZn,
      oldBuhinPrice: oldBuhin.buhinPriceZn
    }
  };
}

/*
 * buildBuhinPriceChangeActionState を WorkAndBuhin.vue から分離し、
 * 単価変更時の選択済み部品価格同期と変更履歴更新の実行条件を固定するため。
 */
export function buildBuhinPriceChangeActionState(params: {
  buhin: BuhinDetail;
  isManual: boolean;
  isDialog: boolean;
  workBeforeChange: any;
  selectedBuhinList: BuhinDetail[];
}): BuhinPriceChangeActionState {
  const priceChange = buildBuhinPriceChange(params);
  const shouldSyncSelectedBuhinPrice = !params.isManual;
  return {
    shouldSyncSelectedBuhinPrice,
    selectedBuhinIndex: shouldSyncSelectedBuhinPrice
      ? findSelectedBuhinIndex(params.selectedBuhinList, params.buhin)
      : -1,
    selectedBuhinPrice: params.buhin.buhinPriceZn || 0,
    shouldApplyPriceChange: !!priceChange,
    buhinChangeId: priceChange ? priceChange.id : null,
    buhinChangeEntry: priceChange ? priceChange.entry : null
  };
}

/*
 * buildBuhinPriceChangeApplyEffectState を WorkAndBuhin.vue から分離し、
 * 単価変更後の選択済み部品価格同期と変更履歴反映の条件・値を固定するため。
 */
export function buildBuhinPriceChangeApplyEffectState(
  actionState: BuhinPriceChangeActionState
): BuhinPriceChangeApplyEffectState {
  return {
    shouldSyncSelectedBuhinPrice: actionState.shouldSyncSelectedBuhinPrice,
    shouldApplySelectedBuhinPrice: (
      actionState.shouldSyncSelectedBuhinPrice &&
      actionState.selectedBuhinIndex > -1
    ),
    selectedBuhinIndex: actionState.selectedBuhinIndex,
    selectedBuhinPrice: actionState.selectedBuhinPrice,
    shouldApplyPriceChange: actionState.shouldApplyPriceChange,
    buhinChangeId: actionState.buhinChangeId,
    buhinChangeEntry: actionState.buhinChangeEntry
  };
}

/*
 * buildEditResultActionState を WorkAndBuhin.vue から分離し、
 * 編集ボタン押下時の event 伝播停止と emit payload を固定するため。
 */
export function buildEditResultActionState(work: any): EditResultActionState {
  return {
    shouldStopPropagation: true,
    shouldEmitOnEditResult: true,
    payload: work
  };
}

/*
 * buildEditResultApplyEffectState を WorkAndBuhin.vue から分離し、
 * 編集結果リンク押下時の event 伝播停止と emit event/payload を固定するため。
 */
export function buildEditResultApplyEffectState(
  actionState: EditResultActionState
): EditResultApplyEffectState {
  return {
    shouldStopPropagation: actionState.shouldStopPropagation,
    shouldEmitOnEditResult: actionState.shouldEmitOnEditResult,
    eventName: "onEditResult",
    payload: actionState.payload
  };
}

/*
 * buildBuhinNameInputClickActionState を WorkAndBuhin.vue から分離し、
 * 部品名入力欄クリック時に親要素の選択処理へ伝播させない条件を固定するため。
 */
export function buildBuhinNameInputClickActionState(): BuhinNameInputClickActionState {
  return {
    shouldStopPropagation: true
  };
}

/*
 * removeEditModeFromData を WorkAndBuhin.vue から分離し、
 * 編集モードと価格変更追跡 を親側置換や単体テストで固定するため。
 */
export function removeEditModeFromData(data: any): void {
  if (data.manualSeibiList && data.manualSeibiList.length !== 0) {
    data.manualSeibiList.map((item: any) => delete item.editMode);
  }
  if (data.seibiList && data.seibiList.length !== 0) {
    data.seibiList.forEach((item: any) => {
      if (item.manualBuhinList && item.manualBuhinList.length !== 0) {
        item.manualBuhinList.map((buhin: any) => delete buhin.editMode);
      }
    });
  }
  if (data.manualSeibiList && data.manualSeibiList.length !== 0) {
    data.manualSeibiList.forEach((item: any) => {
      if (item.manualBuhinList && item.manualBuhinList.length !== 0) {
        item.manualBuhinList.map((buhin: any) => delete buhin.editMode);
      }
    });
  }
  if (data.manualBuhinList && data.manualBuhinList.length !== 0) {
    data.manualBuhinList.map((item: any) => delete item.editMode);
  }
}
